// File: src/MathTools/PiecewiseGraphTool.js
//----------------------------------------------------------
function parseEquation(equationString) {
  // eslint-disable-next-line no-new-func
  return new Function("x", `return ${equationString};`);
}

export default function PiecewiseGraphTool({
  pieces = [],
  width = 400,
  height = 300,
  margin = 30,
  showAxes = true,
  showTicks = true,
  maxTicks = 25,           // maximum ticks allowed on each axis
  xTickStep = 1,           // integer step for x ticks
  yTickStep = 1            // integer step for y ticks
}) {
  // 1) Gather all x,y points from each piece => determine yMin..yMax
  let allPoints = [];
  pieces.forEach(piece => {
    const { equation, xMin, xMax } = piece;
    const fn = parseEquation(equation);
    // sample ~50 points in [xMin..xMax]
    const stepCount = 50;
    const step = (xMax - xMin) / stepCount;
    let localPoints = [];
    for (let xx = xMin; xx <= xMax; xx += step) {
      localPoints.push({ x: xx, y: fn(xx) });
    }
    // ensure the final endpoint is included
    localPoints.push({ x: xMax, y: fn(xMax) });
    // label them by piece
    allPoints.push(...localPoints.map(pt => ({ ...pt, piece })));
  });

  if (!allPoints.length) {
    return <div>No pieces to plot</div>;
  }

  // 2) find global xMinGlobal..xMaxGlobal, yMin..yMax
  let xMinGlobal = Infinity, xMaxGlobal = -Infinity;
  let yMin = Infinity, yMaxi = -Infinity;
  allPoints.forEach(pt => {
    if (pt.x < xMinGlobal) xMinGlobal = pt.x;
    if (pt.x > xMaxGlobal) xMaxGlobal = pt.x;
    if (pt.y < yMin) yMin = pt.y;
    if (pt.y > yMaxi) yMaxi = pt.y;
  });
  // padding
  const padY = 0.1 * (yMaxi - yMin);
  yMin -= padY;
  yMaxi += padY;

  // 3) set up transforms
  const usableWidth = width - 2 * margin;
  const usableHeight = height - 2 * margin;

  function mapXtoSvg(xVal) {
    const ratio = (xVal - xMinGlobal) / (xMaxGlobal - xMinGlobal);
    return margin + ratio * usableWidth;
  }
  function mapYtoSvg(yVal) {
    const ratio = (yVal - yMin) / (yMaxi - yMin);
    return margin + (1 - ratio) * usableHeight;
  }

  // 4) Build paths for each piece
  const piecePaths = pieces.map((p, idx) => {
    const subPts = allPoints.filter(pt => pt.piece === p);
    if (!subPts.length) return null;
    const pathD = subPts.map((pt, i) => {
      const sx = mapXtoSvg(pt.x);
      const sy = mapYtoSvg(pt.y);
      return i === 0 ? `M ${sx},${sy}` : `L ${sx},${sy}`;
    }).join(" ");
    return (
      <path
        key={`piecepath-${idx}`}
        d={pathD}
        fill="none"
        stroke="blue"
        strokeWidth={2}
      />
    );
  });

  // 5) Draw boundary circles (open or closed)
  let boundaryCircles = [];
  pieces.forEach((p, idx) => {
    const fn = parseEquation(p.equation);
    // left boundary
    const leftVal = fn(p.xMin);
    if (p.closedLeft || p.openLeft === false) {
      // filled circle
      boundaryCircles.push(
        <circle
          key={`leftClosed-${idx}`}
          cx={mapXtoSvg(p.xMin)}
          cy={mapYtoSvg(leftVal)}
          r={4}
          fill="blue"
        />
      );
    } else if (p.openLeft) {
      // hollow circle
      boundaryCircles.push(
        <circle
          key={`leftOpen-${idx}`}
          cx={mapXtoSvg(p.xMin)}
          cy={mapYtoSvg(leftVal)}
          r={4}
          fill="white"
          stroke="blue"
          strokeWidth={2}
        />
      );
    }
    // right boundary
    const rightVal = fn(p.xMax);
    if (p.closedRight || p.openRight === false) {
      boundaryCircles.push(
        <circle
          key={`rightClosed-${idx}`}
          cx={mapXtoSvg(p.xMax)}
          cy={mapYtoSvg(rightVal)}
          r={4}
          fill="blue"
        />
      );
    } else if (p.openRight) {
      boundaryCircles.push(
        <circle
          key={`rightOpen-${idx}`}
          cx={mapXtoSvg(p.xMax)}
          cy={mapYtoSvg(rightVal)}
          r={4}
          fill="white"
          stroke="blue"
          strokeWidth={2}
        />
      );
    }
  });

  // 6) Minimal axis lines if showAxes
  let xAxisLine = null, yAxisLine = null;
  if (showAxes) {
    // if 0 is in [yMin..yMaxi], draw horizontal axis
    if (yMin < 0 && yMaxi > 0) {
      xAxisLine = (
        <line
          x1={mapXtoSvg(xMinGlobal)}
          y1={mapYtoSvg(0)}
          x2={mapXtoSvg(xMaxGlobal)}
          y2={mapYtoSvg(0)}
          stroke="gray"
          strokeWidth={1}
        />
      );
    }
    // if 0 in [xMinGlobal..xMaxGlobal], draw vertical axis
    if (xMinGlobal < 0 && xMaxGlobal > 0) {
      yAxisLine = (
        <line
          x1={mapXtoSvg(0)}
          y1={mapYtoSvg(yMin)}
          x2={mapXtoSvg(0)}
          y2={mapYtoSvg(yMaxi)}
          stroke="gray"
          strokeWidth={1}
        />
      );
    }
  }

  // 7) Ticks if showTicks
  let xTicksEls = [];
  let yTicksEls = [];
  if (showTicks) {
    // x ticks: integer from ceil(xMinGlobal) to floor(xMaxGlobal)
    let xTicks = [];
    const xStart = Math.ceil(xMinGlobal);
    const xEnd = Math.floor(xMaxGlobal);
    for (let xv = xStart; xv <= xEnd; xv += xTickStep) {
      xTicks.push(xv);
    }
    // if too many, reduce
    if (xTicks.length > maxTicks) {
      const ratio = Math.ceil(xTicks.length / maxTicks);
      let filtered = [];
      for (let i = 0; i < xTicks.length; i += ratio) {
        filtered.push(xTicks[i]);
      }
      xTicks = filtered;
    }
    // build x tick elements
    // we place them on the “closest” axis line or fallback
    xTicksEls = xTicks.map((xt, i) => {
      let axisY = 0; // default if 0 in range
      if (!(yMin < 0 && yMaxi > 0)) {
        // if 0 not in [yMin..yMaxi], pick yMin or yMaxi
        axisY = yMin; 
      }
      const sx = mapXtoSvg(xt);
      const sy = mapYtoSvg(axisY);
      return (
        <g key={`xtick-${i}`}>
          <line
            x1={sx}
            y1={sy - 5}
            x2={sx}
            y2={sy + 5}
            stroke="black"
            strokeWidth={1}
          />
          <text
            x={sx}
            y={sy + 18}
            fontSize={12}
            textAnchor="middle"
            fill="black"
          >
            {xt}
          </text>
        </g>
      );
    });

    // y ticks
    let yTicks = [];
    const yStart = Math.ceil(yMin);
    const yEnd = Math.floor(yMaxi);
    for (let yv = yStart; yv <= yEnd; yv += yTickStep) {
      yTicks.push(yv);
    }
    if (yTicks.length > maxTicks) {
      const ratio = Math.ceil(yTicks.length / maxTicks);
      let filtered = [];
      for (let i = 0; i < yTicks.length; i += ratio) {
        filtered.push(yTicks[i]);
      }
      yTicks = filtered;
    }
    yTicksEls = yTicks.map((yt, i) => {
      let axisX = 0;
      if (!(xMinGlobal < 0 && xMaxGlobal > 0)) {
        axisX = xMinGlobal;
      }
      const sx = mapXtoSvg(axisX);
      const sy = mapYtoSvg(yt);
      return (
        <g key={`ytick-${i}`}>
          <line
            x1={sx - 5}
            y1={sy}
            x2={sx + 5}
            y2={sy}
            stroke="black"
            strokeWidth={1}
          />
          <text
            x={sx - 8}
            y={sy + 4}
            fontSize={12}
            textAnchor="end"
            fill="black"
          >
            {yt}
          </text>
        </g>
      );
    });
  }

  return (
    <div style={{ textAlign: "center" }}>
      <svg width={width} height={height} style={{ border:"1px solid #ccc" }}>
        {/* axis lines */}
        {xAxisLine}
        {yAxisLine}

        {/* x-axis ticks */}
        {xTicksEls}
        {/* y-axis ticks */}
        {yTicksEls}

        {/* piecewise paths */}
        {piecePaths}
        {/* boundary circles */}
        {boundaryCircles}
      </svg>
      <div style={{ marginTop:8, fontSize:"0.9em"}}>
        <em>PiecewiseGraphTool demo</em>
      </div>
    </div>
  );
}
